import "./ProfileSelectorDropdown.scss";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCountryFlag } from "../../../common/util/getCountryFlag";
import { IconChevronDown } from "../../../common/icons/IconChevronDown/IconChevronDown";
import { ProfileSelectorMenu } from "./ProfileSelectorMenu/ProfileSelectorMenu";
import defaultContent from "../../../content/profileSelectorDropdown";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useLazyGetBusinessSelectorQuery } from "../../../services/MyBizShell/mybizShellAPI";
import { useGetABOEnrichmentDataMutation } from "../../../services/AboEnrichment/AboEnrichmentAPI";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { mapBusinessSelectorData } from "../../BusinessSelector/BusinessSelector";
import { storedLocale } from "../../Authentication/util";
import type { Business } from "../../../services/MyBizShell/mybizShellAPI.types";
import { setAboName } from "../../../reducers/aboName";
import { setNavigatedByBusiness } from "../../../reducers/businessSelector";
import { setBootLoading } from "../../../reducers/boot";
import { SetBootForCurrentBusiness } from "../../../reducers/model/boot";
import { setRedirectTo } from "../../../reducers/componentLoader";

const BUSINESS_CARD_KPIS = [KPI_ID.GROUP_PV, KPI_ID.BONUS_PERCENT, KPI_ID.QUALIFIED_LEGS];

interface ProfileSelectorDropdownProps {
  accountLabel?: string;
  initialOpen?: boolean;
}

export const ProfileSelectorDropdown = ({ accountLabel = "", initialOpen = false }: ProfileSelectorDropdownProps) => {
  const { t } = useTranslation(["profileSelectorDropdown"]);
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(initialOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFetched = useRef(false);

  const { aff, abo: aboNum, isoCountryCode } = useAppSelector((state) => state.boot.user);
  const selectedPeriod = useAppSelector((state) => state.period.selectedPeriod);
  const displayLabel = aboNum && isoCountryCode ? `${isoCountryCode} #${aboNum}` : accountLabel;
  const cachedBusinesses = useAppSelector((state) => state.businessSelectorData.businessSelectorData);

  const [triggerGetBusinessSelector, { data: queryData, isLoading, isError }] = useLazyGetBusinessSelectorQuery();
  const [fetchEnrichmentData] = useGetABOEnrichmentDataMutation();

  const [enrichedBusinesses, setEnrichedBusinesses] = useState<Business[] | null>(null);

  const rawBusinesses =
    cachedBusinesses && cachedBusinesses.length > 0
      ? cachedBusinesses
      : queryData
        ? mapBusinessSelectorData(queryData.businesses)
        : [];

  useEffect(() => {
    if (rawBusinesses.length === 0) return;
    setEnrichedBusinesses(null);
    let cancelled = false;
    const affAbos = rawBusinesses.map((b: Business) => `${b.aff}-${b.abo.replace(/^#/, "")}`);
    void fetchEnrichmentData({
      affAbos,
      bonusPeriod: selectedPeriod,
      locale: storedLocale(),
      isoCountryCode: isoCountryCode || "US",
      kpis: BUSINESS_CARD_KPIS,
    })
      .unwrap()
      .then((result) => {
        if (cancelled) return;
        const kpiMap = new Map(result.aboKpiData.map((d) => [`${d.aff}-${d.abo}`, d]));
        const merged = rawBusinesses.map((b: Business) => {
          const normalizedAbo = b.abo.replace(/^#/, "");
          const kpi = kpiMap.get(`${b.aff}-${normalizedAbo}`);
          if (!kpi) return b;
          return {
            ...b,
            groupPv: kpi.groupPV?.total?.formatted,
            qualifiedLegs: kpi.qualifiedLegs?.formatted,
            bonusPercent: kpi.bonusPercent?.formatted,
          };
        });
        setEnrichedBusinesses(merged);
      })
      .catch(() => {
        if (!cancelled) setEnrichedBusinesses(rawBusinesses);
      });
    return () => {
      cancelled = true;
    };
  }, [rawBusinesses.length, selectedPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  const businesses = enrichedBusinesses ?? rawBusinesses;
  const isKpiLoading = enrichedBusinesses === null && rawBusinesses.length > 0;

  const handleSubPanelOpen = () => {
    if (!hasFetched.current && (!cachedBusinesses || cachedBusinesses.length === 0)) {
      hasFetched.current = true;
      void triggerGetBusinessSelector({ aff, abo: aboNum });
    }
  };

  const handleSelectBusiness = (business: Business) => {
    dispatch(setAboName(business.aboName));
    dispatch(setNavigatedByBusiness(true));
    dispatch(setBootLoading(true));
    SetBootForCurrentBusiness({
      user: { aff: business.aff, abo: business.abo.replace(/^#/, ""), isoCountryCode: business.isoCountry },
    });
    dispatch(setRedirectTo(""));
    setIsOpen(false);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="ProfileSelectorDropdown" ref={containerRef} data-testid="profile-selector-dropdown">
      <button
        type="button"
        className="ProfileSelectorDropdown__trigger"
        aria-label={t("accountSelectorLabel", defaultContent.accountSelectorLabel)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={handleToggle}
        data-testid="profile-selector-trigger"
      >
        <div className="ProfileSelectorDropdown__abo-container">
          <span className="ProfileSelectorDropdown__abo-container__flag" aria-hidden="true">
            {getCountryFlag(isoCountryCode || "US")}
          </span>
          <span>{displayLabel}</span>
        </div>
        <span
          className={`ProfileSelectorDropdown__chevron${isOpen ? " ProfileSelectorDropdown__chevron--open" : ""}`}
          aria-hidden="true"
        >
          <IconChevronDown color="white" />
        </span>
      </button>

      {isOpen && (
        <ProfileSelectorMenu
          onClose={() => setIsOpen(false)}
          businesses={businesses}
          isLoading={isLoading}
          isError={isError}
          isKpiLoading={isKpiLoading}
          onSelectBusiness={handleSelectBusiness}
          onBusinessSubPanelOpen={handleSubPanelOpen}
        />
      )}
    </div>
  );
};
